/**
 * The only place GSAP is imported from. Everyone else writes:
 *
 * ```ts
 * import { gsap, ScrollTrigger, SplitText, useGSAP, EASE, DURATION } from '@/lib/gsap'
 * ```
 *
 * Importing this module registers the plugins, creates the `vk.*` eases and sets the
 * defaults, so every tween in the app shares one motion language. Need another plugin
 * (Observer, Flip, Draggable, ...)? Ask for it to be added here; don't register it locally.
 *
 * @module
 */
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { useAppStore } from './store'

gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase, useGSAP)

// No refresh on window `load`: it lands while the preloader still covers the page and
// re-lays-out everything. SmoothScroll refreshes after fonts load and at `ready` instead.
// Browser only: without a window ScrollTrigger never initialises (tests, tooling).
if (typeof window !== 'undefined') {
  ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,resize' })
}

/** Named eases. They mirror the `--ease-*` CSS tokens. */
export const EASE = {
  /** cubic-bezier(0.16, 1, 0.3, 1): the default. Reveals, UI, almost everything. */
  out: 'vk.out',
  /** cubic-bezier(0.65, 0, 0.35, 1): symmetric moves, morphs, camera travel. */
  inOut: 'vk.inOut',
  /** cubic-bezier(0.7, 0, 0.84, 0): exits only. */
  in: 'vk.in',
} as const

/** Durations in seconds. They mirror the `--dur-*` CSS tokens. */
export const DURATION = {
  micro: 0.2,
  ui: 0.45,
  reveal: 0.9,
  cinematic: 1.4,
} as const

CustomEase.create(EASE.out, '0.16,1,0.3,1')
CustomEase.create(EASE.inOut, '0.65,0,0.35,1')
CustomEase.create(EASE.in, '0.7,0,0.84,0')

gsap.defaults({ ease: EASE.out, duration: DURATION.reveal })

let refreshFrame = 0

/**
 * Re-sorts and refreshes every ScrollTrigger on the next animation frame. Calls in the
 * same frame collapse into one. Use it after layout changes that ScrollTrigger cannot
 * see: fonts loading, images or models changing a section's height, or content appearing.
 *
 * During `boot` the page is covered and scroll-locked, so calls are dropped: SmoothScroll
 * refreshes once when the phase reaches `ready`. Each refresh re-lays-out the whole page,
 * and several of them while the hero scene initialises were costing phones seconds.
 */
export function refreshScroll(): void {
  if (typeof window === 'undefined' || refreshFrame) return
  if (useAppStore.getState().phase === 'boot') return
  refreshFrame = window.requestAnimationFrame(() => {
    refreshFrame = 0
    ScrollTrigger.sort()
    ScrollTrigger.refresh()
  })
}

export { CustomEase, gsap, ScrollTrigger, SplitText, useGSAP }
